import {
  ChangeDetectionStrategy, Component, EventEmitter,
  Input, OnChanges, Output, SimpleChanges, signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  BookingDay, ChiliPiperSlot,
} from '../../../features/pcheck/models/pcheck.model';
import {
  addCalendarDaysYmd, browserTimezone, dateKeyInTimeZone, execTimezoneAbbr,
  firstInstantOnZonedDate, formatSlotTimeInZone, formatZonedDateTimeLine, isIsoUtc,
} from '../../../features/pcheck/helpers/booking-timezone';
import { TimezoneSelectComponent } from '../timezone-select/timezone-select.component';

type QuickChip = 'today' | 'this-week' | 'next-week' | 'all';

export interface SlotSelectedEvent {
  slot: ChiliPiperSlot;
  routeId: string;
  execEmail: string;
  formData?: Record<string, string>;
  timezone: string;
}

@Component({
  selector: 'app-booking-calendar',
  standalone: true,
  imports: [CommonModule, TimezoneSelectComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './booking-calendar.component.html',
  styleUrls: ['./booking-calendar.component.scss'],
})
export class BookingCalendarComponent implements OnChanges {
  // ── Inputs ─────────────────────────────────────────────────────────────────
  @Input() slots: ChiliPiperSlot[] = [];
  @Input() routeId = '';
  @Input() execEmail = '';
  @Input() execName = '';
  @Input() duration = '';
  @Input() formData?: Record<string, string>;

  /** True once a booking has been confirmed — shows the confirmed card. */
  @Input() confirmed = false;
  @Input() confirmedTime?: string;

  /** Disable all interactions (e.g. this is a past message). */
  @Input() disabled = false;
  /** True while the backend is processing the selected slot. */
  @Input() isConfirming = false;
  /** Event-local timezone from backend config (IANA string, e.g. "Asia/Makassar"). */
  @Input() eventTzIana?: string;
  /** Human-readable label for that timezone (e.g. "Bali, Indonesia local time"). */
  @Input() eventTzLabel?: string;
  /** When true (IMPACT_PCHECK), locks the timezone selector to the single
   *  config zone from eventTzIana; the user cannot change it. */
  @Input() lockTimezone = false;

  /** Confirmed booking time rendered in the picker's display zone (Impact: the
   *  event zone) with a short zone label — never the raw UTC ISO string.
   *  Pre-formatted legacy strings pass through untouched. */
  get confirmedTimeLabel(): string {
    const raw = this.confirmedTime ?? '';
    if (!isIsoUtc(raw)) return raw;
    const tz = this.displayTimezone();
    return `${formatZonedDateTimeLine(raw, tz)} (${execTimezoneAbbr(tz)})`;
  }
  /** 'book' (default) or 'reschedule' — varies the copy/labels only. The slot
   *  grid, date strip and timezone logic are identical for both. */
  @Input() mode: 'book' | 'reschedule' = 'book';

  get headerVerb(): string {
    return this.mode === 'reschedule' ? 'Reschedule with' : 'Schedule with';
  }
  get confirmedLabel(): string {
    return this.mode === 'reschedule' ? 'Meeting rescheduled' : 'Meeting booked';
  }
  get slotActionVerb(): string {
    return this.mode === 'reschedule' ? 'Reschedule to' : 'Book at';
  }

  // ── Outputs ────────────────────────────────────────────────────────────────
  @Output() slotSelected = new EventEmitter<SlotSelectedEvent>();

  // ── Internal state ─────────────────────────────────────────────────────────
  // Default to the user's local browser timezone. If the backend supplies an
  // event timezone (eventTzIana — e.g. IMPACT's fixed Bali zone) it overrides
  // this default in ngOnChanges, but the user can still switch to any zone.
  readonly displayTimezone   = signal(browserTimezone());
  readonly selectedDateKey   = signal<string | null>(null);
  readonly rangeInputOpen    = signal(false);
  readonly activeQuickChip   = signal<QuickChip | null>('all');
  readonly rangeFrom         = signal<string | null>(null);
  readonly rangeTo           = signal<string | null>(null);
  readonly rangeInputFrom    = signal('');
  readonly rangeInputTo      = signal('');
  readonly slotPending       = signal(false);

  /** Number of day tiles shown per page in the date strip. The left/right
   *  arrows move this window across the (now up to a year of) available days. */
  readonly STRIP_PAGE = 7;
  /** Index into visibleDays of the first tile currently shown in the strip. */
  readonly stripOffset = signal(0);

  // ── Derived ────────────────────────────────────────────────────────────────

  get bookingDays(): BookingDay[] {
    if (!this.slots.length) return [];
    const tz = this.displayTimezone();
    const seen = new Set<string>();
    const days: BookingDay[] = [];
    const todayKey = dateKeyInTimeZone(new Date(), tz);

    for (const slot of this.slots) {
      const key = dateKeyInTimeZone(new Date(slot.startTime), tz);
      if (!seen.has(key)) {
        seen.add(key);
        const inst = firstInstantOnZonedDate(key, tz);
        days.push({
          dateKey:  key,
          dayName:  inst.toLocaleDateString('en-US', { timeZone: tz, weekday: 'short' }),
          dayNum:   Number.parseInt(inst.toLocaleDateString('en-US', { timeZone: tz, day: 'numeric' }), 10),
          month:    inst.toLocaleDateString('en-US', { timeZone: tz, month: 'short' }),
          isToday:  key === todayKey,
          hasSlots: true,
        });
      }
    }
    return days;
  }

  get visibleDays(): BookingDay[] {
    const from = this.rangeFrom();
    const to   = this.rangeTo();
    if (!from || !to) return this.bookingDays;
    return this.bookingDays.filter(d => d.dateKey >= from && d.dateKey <= to);
  }

  /** The slice of visibleDays currently shown in the date strip (one page). */
  get pagedDays(): BookingDay[] {
    const days = this.visibleDays;
    const maxStart = Math.max(0, days.length - this.STRIP_PAGE);
    const start = Math.min(this.stripOffset(), maxStart);
    return days.slice(start, start + this.STRIP_PAGE);
  }

  get canPageLeft(): boolean {
    return this.stripOffset() > 0;
  }

  get canPageRight(): boolean {
    return this.stripOffset() + this.STRIP_PAGE < this.visibleDays.length;
  }

  get rangeLabel(): string {
    const from = this.rangeFrom();
    const to   = this.rangeTo();
    if (!from || !to) {
      const days = this.bookingDays;
      if (!days.length) return 'No availability';
      const last = days.at(-1);
      return `${days[0].dayName} ${days[0].dayNum} ${days[0].month} – ${last?.dayName} ${last?.dayNum} ${last?.month}`;
    }
    return `${from} – ${to}`;
  }

  get hasActiveRange(): boolean {
    return !!(this.rangeFrom() && this.rangeTo() && this.activeQuickChip() !== 'all');
  }

  get minDateKey(): string {
    return this.bookingDays[0]?.dateKey ?? '';
  }

  get maxDateKey(): string {
    return this.bookingDays.at(-1)?.dateKey ?? '';
  }

  getSlotsForDate(dateKey: string): ChiliPiperSlot[] {
    const tz = this.displayTimezone();
    return this.slots.filter(s => dateKeyInTimeZone(new Date(s.startTime), tz) === dateKey);
  }

  formatTime(isoUtc: string): string {
    return formatSlotTimeInZone(isoUtc, this.displayTimezone());
  }

  getMeetingMinutes(): number {
    if (!this.duration) return 30;
    const m = /(\d+)/.exec(this.duration);
    return m ? Number.parseInt(m[1], 10) : 30;
  }

  // ── ngOnChanges ────────────────────────────────────────────────────────────

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['eventTzIana']) {
      // A backend-supplied event timezone (e.g. IMPACT's fixed zone) becomes the
      // default; absent that, the browser-local default set above stands. Either
      // way the user can switch zones via the searchable picker.
      if (this.eventTzIana) {
        this.displayTimezone.set(this.eventTzIana);
      }
    }
    // Only reset the UI state when the slots list itself is replaced.
    // Other input changes (confirmed, disabled, isConfirming) must NOT
    // clear the user's current date / range selection.
    if (changes['slots']) {
      this.selectedDateKey.set(null);
      this.rangeFrom.set(null);
      this.rangeTo.set(null);
      this.activeQuickChip.set('all');
      this.rangeInputOpen.set(false);
      this.slotPending.set(false);
      this.stripOffset.set(0);
    }
  }

  // ── Handlers ───────────────────────────────────────────────────────────────

  onDateSelect(dateKey: string): void {
    this.selectedDateKey.set(dateKey);
  }

  onPageLeft(): void {
    this.stripOffset.update(o => Math.max(0, o - this.STRIP_PAGE));
  }

  onPageRight(): void {
    const maxStart = Math.max(0, this.visibleDays.length - this.STRIP_PAGE);
    this.stripOffset.update(o => Math.min(maxStart, o + this.STRIP_PAGE));
  }

  /** Move the strip window so the currently-selected day is on the visible page
   *  (used after a timezone change re-anchors the selection). */
  private _snapStripToSelected(): void {
    const key = this.selectedDateKey();
    if (!key) { this.stripOffset.set(0); return; }
    const idx = this.visibleDays.findIndex(d => d.dateKey === key);
    this.stripOffset.set(idx < 0 ? 0 : Math.floor(idx / this.STRIP_PAGE) * this.STRIP_PAGE);
  }

  onTimezoneChange(value: string): void {
    // Capture the currently-shown day's slots in the OLD zone before switching,
    // so we can keep the user anchored on the same availability afterwards.
    const prevKey = this.selectedDateKey();
    const prevSlots = prevKey ? this.getSlotsForDate(prevKey) : [];

    this.displayTimezone.set(value);

    // Changing the zone must NOT clear the day selection — it should just
    // re-render the same slots in the new zone. Re-anchor the selected day to
    // the new-zone date key that holds those slots (a slot can cross the
    // midnight boundary when the zone changes), falling back to the same key if
    // it still exists, else the first available day.
    if (prevKey) {
      let nextKey: string | null = prevSlots.length
        ? dateKeyInTimeZone(new Date(prevSlots[0].startTime), value)
        : null;
      const days = this.bookingDays;
      if (!nextKey || !days.some(d => d.dateKey === nextKey)) {
        nextKey = days.some(d => d.dateKey === prevKey)
          ? prevKey
          : (days[0]?.dateKey ?? null);
      }
      this.selectedDateKey.set(nextKey);
    }
    this._snapStripToSelected();
  }

  onToggleRangeInput(): void {
    this.rangeInputOpen.update(v => !v);
  }

  onResetRange(): void {
    this.rangeFrom.set(null);
    this.rangeTo.set(null);
    this.activeQuickChip.set('all');
    this.rangeInputFrom.set('');
    this.rangeInputTo.set('');
    this.selectedDateKey.set(null);
    this.rangeInputOpen.set(false);
    this.stripOffset.set(0);
  }

  onQuickPick(chip: QuickChip): void {
    this.activeQuickChip.set(chip);
    const tz   = this.displayTimezone();
    const today = dateKeyInTimeZone(new Date(), tz);

    if (chip === 'all') { this.onResetRange(); return; }

    let from = today;
    let to   = today;
    if (chip === 'today') {
      from = today; to = today;
    } else if (chip === 'this-week') {
      const d = new Date();
      const dow = d.getDay();
      from = addCalendarDaysYmd(today, -dow);
      to   = addCalendarDaysYmd(from, 6);
    } else if (chip === 'next-week') {
      const d = new Date();
      const dow = d.getDay();
      from = addCalendarDaysYmd(today, 7 - dow);
      to   = addCalendarDaysYmd(from, 6);
    }

    this.rangeFrom.set(from);
    this.rangeTo.set(to);
    this.rangeInputFrom.set(from);
    this.rangeInputTo.set(to);
    this.selectedDateKey.set(null);
    this.stripOffset.set(0);
  }

  onRangeInputChange(field: 'from' | 'to', value: string): void {
    if (field === 'from') this.rangeInputFrom.set(value);
    else                  this.rangeInputTo.set(value);
  }

  onApplyRange(): void {
    const from = this.rangeInputFrom();
    const to   = this.rangeInputTo();
    if (!from || !to) return;
    // Silently swap if the user entered an inverted range instead of showing
    // an empty result set.
    const [lo, hi] = from <= to ? [from, to] : [to, from];
    this.rangeFrom.set(lo);
    this.rangeTo.set(hi);
    this.rangeInputFrom.set(lo);
    this.rangeInputTo.set(hi);
    this.activeQuickChip.set(null);
    this.selectedDateKey.set(null);
    this.rangeInputOpen.set(false);
    this.stripOffset.set(0);
  }

  onWidenRange(): void {
    this.onResetRange();
  }

  onSlotSelect(slot: ChiliPiperSlot): void {
    if (this.disabled || this.isConfirming || this.slotPending()) return;
    this.slotPending.set(true);
    this.slotSelected.emit({
      slot,
      routeId:   this.routeId,
      execEmail: this.execEmail,
      formData:  this.formData,
      timezone:  this.displayTimezone(),
    });
  }

}
